import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { MainLayout } from '@templates/index';
import { Button, Card, Input, PasswordInput } from '@atoms/index';
import { useAuthStore } from '@store/authStore';
import { ChangePasswordModal } from '@organisms/ChangePasswordModal';
import { storage, STORAGE_KEYS } from '@utils/storage';
import { useCartStore } from '@store/cartStore';
import { useAppConfig } from '@/contexts/AppConfigContext';

function useIsGoogleConfigured() {
  const { googleClientId } = useAppConfig();
  return !!googleClientId;
}

interface GoogleLoginButtonProps {
  isLoading: boolean;
  onError: (msg: string) => void;
  onClearError: () => void;
}

const GoogleLoginButton = ({ isLoading, onError, onClearError }: GoogleLoginButtonProps) => {
  const navigate = useNavigate();
  const { socialLogin } = useAuthStore();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      onClearError();
      setIsGoogleLoading(true);
      try {
        await socialLogin('google', tokenResponse.access_token);
        navigate('/');
      } catch (err: any) {
        onError(err.message || 'Google ile giriş yapılamadı');
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: () => {
      onError('Google ile giriş iptal edildi veya başarısız oldu');
      setIsGoogleLoading(false);
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      fullWidth
      onClick={() => googleLogin()}
      disabled={isLoading || isGoogleLoading}
      className="relative flex items-center justify-center gap-3 border-2 hover:bg-gray-50 transition-all"
    >
      {isGoogleLoading ? (
        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
      <span className="font-medium text-gray-700">Google ile devam et</span>
    </Button>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const isGoogleConfigured = useIsGoogleConfigured();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  useEffect(() => {
    const handleMustChangePassword = () => {
      setShowChangePasswordModal(true);
    };

    window.addEventListener('auth:mustChangePassword', handleMustChangePassword as EventListener);

    return () => {
      window.removeEventListener('auth:mustChangePassword', handleMustChangePassword as EventListener);
    };
  }, []);

  const handlePasswordChangeSuccess = async () => {
    setShowChangePasswordModal(false);
    const user = useAuthStore.getState().user;
    if (user) {
      const updatedUser = { ...user, mustChangePassword: false };
      useAuthStore.setState({ user: updatedUser });
      storage.set(STORAGE_KEYS.USER_DATA, updatedUser);
    }
    try {
      await useCartStore.getState().syncFromBackend();
    } catch (cartError) {
      console.error('Failed to sync cart after password change:', cartError);
    }
    navigate('/');
  };

  useEffect(() => {
    if (isAuthenticated && !useAuthStore.getState().user?.mustChangePassword) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoginError(null);

    if (!email.trim()) {
      setLoginError('E-posta adresi gerekli');
      return;
    }

    if (!password.trim()) {
      setLoginError('Şifre gerekli');
      return;
    }

    try {
      await login(email.trim(), password);
      navigate('/');
    } catch (err: any) {
      setLoginError(err.message || 'Giriş başarısız oldu');
    }
  };


  return (
    <MainLayout>
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Premium Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-premium mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Lezzet Yolda'ya Hoş Geldin! 🎉
            </h1>
            <p className="text-gray-600 text-lg">
              Lezzetli yemekler bir tık uzağında
            </p>
          </div>

          {/* Premium Card */}
          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {(error || loginError) && (
                <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-error font-medium">{error || loginError}</p>
                  </div>
                </div>
              )}

              {/* Email/Password Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Şifre
                  </label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    className="w-full"
                  />
                </div>

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Şifremi Unuttum
                  </button>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || !email.trim() || !password.trim()}
                  isLoading={isLoading}
                >
                  Giriş Yap
                </Button>
              </form>

              {isGoogleConfigured && (
                <>
                  <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">veya</span>
                    </div>
                  </div>

                  <GoogleLoginButton
                    isLoading={isLoading}
                    onError={(msg) => setLoginError(msg)}
                    onClearError={() => { clearError(); setLoginError(null); }}
                  />
                </>
              )}

              {/* Register Link */}
              <div className="text-center mt-6">
                <p className="text-sm text-gray-600 mb-2">
                  Hesabınız yok mu?{' '}
                  <button
                    onClick={() => navigate('/register')}
                    className="text-primary hover:text-primary/80 font-medium transition-colors underline"
                  >
                    Kayıt Ol
                  </button>
                </p>
              </div>

              {/* Guest Mode Info */}
              <div className="text-center mt-4">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  Giriş yapmadan devam et →
                </button>
                <p className="mt-2 text-xs text-gray-400">
                  Giriş yapmadan da sipariş verebilirsiniz
                </p>
              </div>
            </div>
            
            {/* Premium Footer */}
            <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Devam ederek{' '}
                <a href="/terms" className="text-primary font-medium hover:underline">
                  Kullanım Koşulları
                </a>
                {' '}ve{' '}
                <a href="/privacy" className="text-primary font-medium hover:underline">
                  Gizlilik Politikası
                </a>
                'nı kabul etmiş olursunuz.
              </p>
            </div>
          </Card>

          {/* Trust Badges */}
          <div className="mt-8 flex items-center justify-center gap-6 text-gray-400 animate-fade-in">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Güvenli</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Şifreli</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm">Premium</span>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => {}}
        onSuccess={handlePasswordChangeSuccess}
        showBackButton={false}
      />
    </MainLayout>
  );
};

export default LoginPage;
